import { createStore } from '@/common/pinia';
import type { ApiResponse } from '@/common/services/api.service-new';
import { alertFreshnessAt, oldestRefreshedAt } from '../alerts/alert-freshness';
import type { ZoneAlertResponse } from '../alerts/zone-batch.client';
import {
  DEFAULT_DEFORESTATION_LAYER,
  type AlertsRaddZone,
  type DeforestationLayerChoice,
  type GladRaddLayer,
} from './deforestation.types';

/**
 * Per-zone alert cache key. A zone's alerts differ by product (RADD radar vs
 * GLAD-L optical vs combined), so the cache is keyed by both. The layer choice
 * doubles as the URL path segment (`/radd/zone`, `/glad/zone`, `/glad-radd/zone`).
 */
export const deforestationAlertCacheKey = (
  zoneId: number,
  layer: DeforestationLayerChoice,
): string => `${layer}:${zoneId}`;

class DeforestationStore {
  // Per-zone alert cache, keyed by `${layer}:${zoneId}` (see
  // `deforestationAlertCacheKey`) since a zone's alerts differ by product.
  // Stamped with `refreshedAt` / `staleAfter` by the batch client, which owns expiry.
  public alerts: { [key: string]: ZoneAlertResponse<AlertsRaddZone> | null } = {};
  public layer: ApiResponse<{ tileUrl: string }> | null = null;
  // GLAD-L only (Landsat optical) deforestation tile layer — see `/glad/layer`.
  // Parallel to and independent from `layer` (RADD) and `gladRaddLayer` (combined).
  public gladLayer: ApiResponse<{ tileUrl: string }> | null = null;
  // RADD + GLAD-L integrated ("combined") deforestation alert tile layer. Parallel
  // to and independent from `layer` (the plain RADD tiles) — see `/glad-radd/layer`.
  public gladRaddLayer: ApiResponse<GladRaddLayer> | null = null;
  // Per-zone email-subscription state — mirrors `FireStore.subscriptions`,
  // kept fully independent from the fire subscriptions.
  public subscriptions: { [zoneId: number]: boolean } = {};
  // Per-parent-zone deforestation layer choice (`radd` vs `glad-radd`), keyed
  // by the parent/`01` zone id. Absent = the RADD default. Read at zone-open by
  // the RADD / GLAD-RADD layer builders to decide which layer to register.
  public layerPreferences: { [parentZoneId: number]: DeforestationLayerChoice } = {};

  public addAlert(key: string, alerts: ZoneAlertResponse<AlertsRaddZone> | null = null): void {
    this.alerts[key] = alerts;
  }

  /** The oldest known `refreshed_at` among these zones on one product, or null. */
  public oldestRefreshedAt(zoneIds: number[], layer: DeforestationLayerChoice): string | null {
    return oldestRefreshedAt(
      zoneIds.map((id) => this.alerts[deforestationAlertCacheKey(id, layer)]?.refreshedAt),
    );
  }

  public oldestCheckedAt(zoneIds: number[], layer: DeforestationLayerChoice): string | null {
    return oldestRefreshedAt(zoneIds.map((id) => {
      const response = this.alerts[deforestationAlertCacheKey(id, layer)];
      return alertFreshnessAt(response?.refreshedAt, response?.checkedAt);
    }));
  }

  public setSubscription(zoneId: number, subscribed: boolean): void {
    this.subscriptions[zoneId] = subscribed;
  }

  public setLayerPreference(parentZoneId: number, layer: DeforestationLayerChoice): void {
    this.layerPreferences[parentZoneId] = layer;
  }

  /** The chosen layer for a parent zone, defaulting to RADD when unset. */
  public layerPreferenceFor(parentZoneId: number): DeforestationLayerChoice {
    return this.layerPreferences[parentZoneId] ?? DEFAULT_DEFORESTATION_LAYER;
  }

  public setLayer(layer: ApiResponse<{ tileUrl: string }> | null = null): void {
    this.layer = layer;
  }

  public setGladLayer(layer: ApiResponse<{ tileUrl: string }> | null = null): void {
    this.gladLayer = layer;
  }

  public setGladRaddLayer(layer: ApiResponse<GladRaddLayer> | null = null): void {
    this.gladRaddLayer = layer;
  }
}

const useDeforestationStore = createStore(DeforestationStore);

export { useDeforestationStore, type DeforestationStore };
